import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLogging } from '../logging';
import { BannerInfo, DeveloperBannerManager, Listener } from './DeveloperBannerManager';

interface BannerInfoWithCount extends BannerInfo {
  count?: number;
}

const bannerStyles = {
  info: 'bg-blue-500 text-white hover:bg-blue-600',
  warning: 'bg-yellow-500 text-black hover:bg-yellow-600',
  error: 'bg-red-500 text-white hover:bg-red-600',
};

const MAX_BANNERS = 3;

export const DeveloperBanners = () => {
  const devToolsEnabled = useMemo(() => window.localStorage.getItem('devtools') === 'true', []);
  const isProductionEnv = useMemo(() => window.location.host === 'platform.gfk.com', []);
  const developerBannerManager = useMemo(() => DeveloperBannerManager.getInstance(), []);
  const [banners, setBanners] = useState<BannerInfoWithCount[]>([]);
  const showingBanners = useMemo(() => banners.slice(-MAX_BANNERS), [banners]);
  const logger = useLogging();

  const listener: Listener = useCallback(
    bannerInfo => {
      if (isProductionEnv || !devToolsEnabled) {
        return;
      }

      const addedBanner = typeof bannerInfo === 'function' ? bannerInfo() : bannerInfo;

      if (!addedBanner) {
        return;
      }

      setBanners(prevBanners => {
        const matchingBanner = prevBanners.find(b => b.message === addedBanner.message);
        if (matchingBanner) {
          return prevBanners.map(b =>
            b === matchingBanner ? { ...matchingBanner, count: (matchingBanner.count ?? 1) + 1 } : b
          );
        }
        logger[addedBanner.level]?.(addedBanner.message as unknown as string & Error, {
          ...addedBanner.properties,
          logType: 'developer-banner',
        });
        return [...prevBanners, addedBanner];
      });
    },
    [devToolsEnabled, isProductionEnv, logger]
  );

  useEffect(() => {
    developerBannerManager.setListener(listener);

    return DeveloperBannerManager.reset;
  }, [developerBannerManager, listener]);

  useEffect(() => {
    const onPopState = () => setBanners([]);
    window.addEventListener('popstate', onPopState);

    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  const onBannerClick = (i: number) => () => {
    const banner = showingBanners[i];
    setBanners(banners.filter(b => b !== banner));
    banner?.redirectTo && window.open(banner.redirectTo, '_blank');
  };

  return (
    <>
      {showingBanners.map((banner, index) => (
        <div
          key={index}
          className={`beta-banner w-full h-4.5 text-xs text-center ${bannerStyles[banner.level]}`}
          role="button"
          tabIndex={0}
          onClick={onBannerClick(index)}
        >
          {banner.message} {banner.count && `(${banner.count})`} {banner.redirectTo && 'Click for more info.'}
        </div>
      ))}
    </>
  );
};
